/* WhyGolf app hero — Instagram-story style carousel.

   Ported from the Replo page's inline srcdoc script. Behaviour kept:
   - video slides advance on `ended`; image slides advance on a 5s timer
   - one rAF loop fills the live progress bar from video currentTime/duration
     (or elapsed timer time), so the bar always tracks real playback
   - if autoplay is blocked the video slide falls back to the timer instead of
     stalling the carousel

   Changed from the original: scoped to the element rather than document IDs,
   so multiple heroes and theme-editor re-renders both work; the rAF loop is
   stopped on disconnect instead of running forever. */

class AppHeroStory extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.app-hero__slide'));
    this.bars = Array.from(this.querySelectorAll('.app-hero__bars i'));
    if (!this.slides.length) return;

    this.index = 0;
    this.slideTimer = null;
    this.timerStart = 0;
    this.timerBased = false;
    this.rafId = null;
    this.imageDuration = Number(this.dataset.imageDuration) || 5000;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.slides.forEach((slide) => {
      const video = slide.querySelector('video');
      if (!video) return;
      video.removeAttribute('loop');
      video.addEventListener('ended', () => {
        if (this.slides[this.index] === slide) this.next();
      });
    });

    this.addEventListener('click', this.onClick);

    this.render();
    if (!this.reduced) this.rafId = requestAnimationFrame(this.tick);
  }

  disconnectedCallback() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    if (this.slideTimer) clearTimeout(this.slideTimer);
    this.removeEventListener('click', this.onClick);
  }

  onClick = (event) => {
    const next = event.target.closest('.app-hero__tap--next, .app-hero__arrow--next');
    const prev = event.target.closest('.app-hero__tap--prev, .app-hero__arrow--prev');
    if (!next && !prev) return;
    event.stopPropagation();
    if (next) this.next();
    else this.prev();
  };

  startTimer() {
    this.timerBased = true;
    this.timerStart = Date.now();
    this.slideTimer = setTimeout(() => this.next(), this.imageDuration);
  }

  render() {
    if (this.slideTimer) {
      clearTimeout(this.slideTimer);
      this.slideTimer = null;
    }
    this.timerBased = false;

    this.slides.forEach((slide, i) => {
      const isCurrent = i === this.index;
      slide.toggleAttribute('hidden', !isCurrent);

      const video = slide.querySelector('video');
      if (!video) return;

      if (!isCurrent) {
        video.pause();
        return;
      }

      try {
        video.currentTime = 0;
      } catch (e) {
        /* Safari throws if metadata is not loaded yet; harmless. */
      }

      const playing = video.play();
      if (playing && playing.catch) {
        // Autoplay blocked — fall back to the timer so the carousel never stalls.
        playing.catch(() => this.startTimer());
      }
    });

    // Reduced motion: hold on the first slide, no auto-advance.
    if (!this.slides[this.index].querySelector('video') && !this.reduced) {
      this.startTimer();
    }

    this.bars.forEach((bar, i) => {
      bar.classList.toggle('is-done', i < this.index);
      bar.classList.toggle('is-live', i === this.index);
      bar.style.setProperty('--p', i < this.index ? '100%' : '0%');
    });
  }

  tick = () => {
    const bar = this.bars[this.index];
    if (bar) {
      let progress = 0;
      if (this.timerBased) {
        progress = (Date.now() - this.timerStart) / this.imageDuration;
      } else {
        const video = this.slides[this.index].querySelector('video');
        if (video && video.duration > 0) progress = video.currentTime / video.duration;
      }
      progress = Math.max(0, Math.min(1, progress));
      bar.style.setProperty('--p', `${(progress * 100).toFixed(2)}%`);
    }
    this.rafId = requestAnimationFrame(this.tick);
  };

  go(n) {
    this.index = Math.max(0, Math.min(this.slides.length - 1, n));
    this.render();
  }

  next() {
    this.go(this.index >= this.slides.length - 1 ? 0 : this.index + 1);
  }

  prev() {
    this.go(this.index <= 0 ? this.slides.length - 1 : this.index - 1);
  }
}

if (!customElements.get('app-hero-story')) {
  customElements.define('app-hero-story', AppHeroStory);
}
